/**
 * Cart utility for managing addon selections
 * Uses localStorage to persist cart data
 */

const CART_KEY = "shopnest_addon_cart";

/**
 * Get cart from localStorage
 * @returns {array} Array of cart items
 */
export const getCart = () => {
  try {
    const cartData = localStorage.getItem(CART_KEY);
    return cartData ? JSON.parse(cartData) : [];
  } catch (error) {
    console.error("Error getting cart:", error);
    return [];
  }
};

/**
 * Add item to cart
 * @param {object} item - { addonTypeId, quantity, duration, price, label }
 */
export const addToCart = (item) => {
  try {
    const cart = getCart();

    // Check if item already exists (by addonTypeId and duration)
    const existingIndex = cart.findIndex(
      (cartItem) =>
        cartItem.addonTypeId === item.addonTypeId &&
        cartItem.duration === item.duration,
    );

    if (existingIndex >= 0) {
      // Update quantity if already exists
      cart[existingIndex].quantity = item.quantity;
    } else {
      // Add new item
      cart.push(item);
    }

    localStorage.setItem(CART_KEY, JSON.stringify(cart));
    return cart;
  } catch (error) {
    console.error("Error adding to cart:", error);
    return [];
  }
};

/**
 * Remove item from cart
 * @param {number} addonTypeId - The addon type ID
 * @param {number} duration - The duration
 */
export const removeFromCart = (addonTypeId, duration) => {
  try {
    let cart = getCart();
    cart = cart.filter(
      (item) =>
        !(item.addonTypeId === addonTypeId && item.duration === duration),
    );
    localStorage.setItem(CART_KEY, JSON.stringify(cart));
    return cart;
  } catch (error) {
    console.error("Error removing from cart:", error);
    return [];
  }
};

/**
 * Clear entire cart
 */
export const clearCart = () => {
  try {
    localStorage.removeItem(CART_KEY);
    return [];
  } catch (error) {
    console.error("Error clearing cart:", error);
    return [];
  }
};

/**
 * Get total price of all items in cart
 * @returns {number} Total price
 */
export const getCartTotal = () => {
  const cart = getCart();
  return cart.reduce((total, item) => total + item.price * item.quantity, 0);
};

/**
 * Get cart item count
 * @returns {number} Number of items in cart
 */
export const getCartCount = () => {
  const cart = getCart();
  return cart.length;
};

/**
 * Sync cart with current addon selections
 * @param {object} addonSelections - { addonName: { checked, quantity, duration, price, addonTypeId } }
 * This function will:
 * - Add items that are checked
 * - Remove items that are unchecked
 * - Update quantities/durations for existing items
 */
export const syncCartWithSelections = (addonSelections) => {
  try {
    const cart = getCart();
    const newCart = [];

    // Process each addon in the selections
    Object.entries(addonSelections).forEach(([addonName, selection]) => {
      if (selection.checked && selection.addonTypeId) {
        // Item is checked - add or update in cart
        const existingIndex = cart.findIndex(
          (cartItem) =>
            cartItem.addonTypeId === selection.addonTypeId &&
            cartItem.duration === selection.duration,
        );

        if (existingIndex >= 0) {
          // Update existing item
          cart[existingIndex].quantity = selection.quantity || 1;
          newCart.push(cart[existingIndex]);
        } else {
          // Add new item
          newCart.push({
            addonTypeId: selection.addonTypeId,
            addonName: addonName,
            quantity: selection.quantity || 1,
            duration: selection.duration || 1,
            durationLabel:
              selection.durationLabel || `${selection.duration || 1} Months`,
            price: selection.price || 0,
            isOnetime: selection.isOnetime || false,
          });
        }
      }
      // If not checked, don't add to cart (item will be removed)
    });

    localStorage.setItem(CART_KEY, JSON.stringify(newCart));
    return newCart;
  } catch (error) {
    console.error("Error syncing cart:", error);
    return [];
  }
};
